import { observer } from "mobx-react-lite";
import type { ReactNode } from "react";
import { Link, useLocation } from "wouter";
import { DEFAULT_LOCALE, type SupportedLocaleType } from "../../common/types/languageTypes";
import { type AlgoliaCmsContentHit, SEARCH_TYPE, type SearchDomain } from "../../common/types/searchTypes";
import contentStore from "../../stores/cms/contentStore";
import searchStore from "../../stores/search/searchStore";
import { getLocalizedString } from "../../stores/search/searchUtils";
import ImageWithFocus from "../cms/ImageWithFocus";
import RecipeImage from "../cms/recipe/RecipeImage";

const hide = () => searchStore.clear(SEARCH_TYPE.QuickSearch, true);

type CmsContentHitProps = {
   hit: AlgoliaCmsContentHit;
   position: number;
   locale: SupportedLocaleType;
   searchDomain: SearchDomain;
   queryID: string;
};

const CmsContentHit = ({ hit, position, locale, searchDomain, queryID }: CmsContentHitProps) => {
   const [location] = useLocation();
   const articleUrlData = contentStore.urlData.data.find((data) => data.id === hit.objectID);
   if (!articleUrlData) {
      return null;
   }
   let image: ReactNode;
   let displayTitle = getLocalizedString(hit.fields?.title || {}, locale);
   const articleUrl = `/${articleUrlData.fullUrl}`;
   if (hit.fields && "headerImage" in hit.fields) {
      const imageId = hit.fields?.headerImage?.[DEFAULT_LOCALE]?.sys.id;
      image = <ImageWithFocus className="rounded-2" placement="aboveFold" width={100} ratio="16x9" entryId={imageId} />;
   }
   if (
      hit.fields &&
      "recipeData" in hit.fields &&
      hit.fields.recipeData &&
      hit.fields.recipeData[DEFAULT_LOCALE] &&
      hit.fields.recipeData[DEFAULT_LOCALE].image
   ) {
      displayTitle = hit.fields.recipeData[DEFAULT_LOCALE].displayName || displayTitle;
      const recipeImage = hit.fields.recipeData[DEFAULT_LOCALE].image;
      if (recipeImage.url && recipeImage.title) {
         image = (
            <RecipeImage
               className="rounded-md"
               placement="aboveFold"
               width={100}
               ratio="16x9"
               url={recipeImage.url}
               title={recipeImage.title}
            />
         );
      }
   }

   const handleClick = (id: string, position: number, url: string) => {
      searchStore.reportAlgoliaClick(id, searchDomain.indexName, queryID, position, true, SEARCH_TYPE.QuickSearch);
      searchStore.reportArticleViewed(id, searchDomain.indexName, queryID);
      if (url === location) {
         hide();
      }
   };
   return (
      <div className="flex items-center justify-between px-1 py-2 hover:bg-[--search-quick-results-item-bg-hover] lg:pr-6">
         <Link to={articleUrl} onClick={() => handleClick(hit.objectID, position, articleUrl)} className="flex items-center">
            {!!image && <div className="min-w-20 max-w-20">{image}</div>}
            <div className="mx-2 grow overflow-ellipsis">{displayTitle}</div>
         </Link>
      </div>
   );
};

export default observer(CmsContentHit);
